// Das Fokusbild — an EINER Stelle (A44).
//
// DER BEFUND (aus A42, gemessen in A44)
//   Es gab drei verschiedene Fokus-Anzeigen im Haus, und die dritte war
//   nicht nur uneinheitlich, sondern falsch:
//
//     1. `ring-2 ring-akzent`                    Glocke, Seitenwechsler,
//                                                BottomNav, Karte …
//     2. `ring-2 ring-akzent ring-offset-2`      components/ui/Button
//     3. `focus:border-akzent`                   ALLE Eingabefelder
//
//   Punkt 3 hing an `:focus` statt `:focus-visible`. `:focus` löst auch
//   bei der MAUS aus: Jedes Feld, das man anklickt, färbte seinen Rand
//   um. Die Anzeige sagte damit nicht „hier bist Du mit der Tastatur",
//   sondern „hier hast Du zuletzt geklickt" — und war als
//   Tastatur-Wegweiser wertlos.
//
// WARUM KLASSEN-KONSTANTEN UND KEINE CSS-KLASSE
//   Das Haus hält Klassenketten schon so (`lib/feldKlassen.ts`, A14.1).
//   Tailwind liest diese Datei mit und erzeugt die Klassen, weil sie
//   wörtlich darin stehen — eine `@layer components`-Klasse müsste die
//   Farbe aus `--akzent` von Hand auflösen und stünde damit neben dem
//   Token statt darauf.
//
// DREI AUSPRÄGUNGEN, EIN BILD
//   Farbe und Stärke sind überall dieselben: 2 px Akzent. Verschieden
//   ist nur, WO der Ring liegt — und das entscheidet nicht der
//   Geschmack, sondern der Untergrund:
//
//     an der Kante   der Regelfall
//     abgesetzt      wenn der Grund des Elements selbst die Akzentfarbe
//                    trägt — sonst verschwindet der Ring darin
//     innen          wenn ein Elternkasten `overflow-hidden` trägt —
//                    sonst schneidet er ihn ab (Befund aus A39)
//
//   Eine Regel über alle zu stülpen wäre in beiden Richtungen falsch:
//   Ein abgesetzter Ring außerhalb eines beschnittenen Kastens ist
//   unsichtbar, und ein Ring an der Kante eines akzentfarbenen Knopfes
//   ebenso.
//
//   (Der abgesetzte Ring heißt in Tailwind „ring-offset-2". Diese Datei
//   schreibt den Namen NIRGENDS ohne seine Zahl — auch nicht im
//   Fließtext: Tailwind liest Kommentare nach Klassennamen ab, und ein
//   solcher Name ohne Wert hat den Übersetzungslauf mit „[postcss]
//   Cannot read properties of undefined" abgebrochen. Der Fehler zeigte
//   dabei auf index.css statt auf diese Zeile.)

/** Gemeinsamer Kern: nirgends `:focus`, überall `:focus-visible`. */
const KERN = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-akzent';

/**
 * Der Regelfall: der Ring liegt direkt an der Kante des Elements.
 *
 * Er kommt ohne Abstandsfarbe aus und funktioniert deshalb auf jedem
 * Untergrund — auch auf dem dunklen des Scan-Fensters.
 */
export const FOKUS_RING = KERN;

/**
 * Für Elemente, deren eigene Fläche die Akzentfarbe trägt.
 *
 * Ohne den Spalt läge Teal auf Teal, und der Ring wäre nicht zu sehen.
 * `ring-offset-cream` ist die Farbe der Hauptfläche — dort stehen diese
 * Knöpfe.
 */
export const FOKUS_RING_ABGESETZT = `${KERN} focus-visible:ring-offset-2 focus-visible:ring-offset-cream`;

/**
 * Für den dunklen Grund des Scan-Fensters.
 *
 * GERECHNET, NICHT GESCHÄTZT: Der Akzentring auf Ink (#2B2B2B) steht
 * bei 2,27:1 und reißt damit die 3:1 für Grafik (E40). Cream auf Ink
 * sind 13,70:1. Auf hellem Grund wäre es umgekehrt — deshalb steht
 * dieser Ring NUR dort, wo die Fläche dunkel ist, und nicht als zweite
 * Wahlmöglichkeit daneben.
 *
 * Es ist dasselbe Bild: 2 px, `focus-visible`, an der Kante. Nur die
 * Farbe folgt dem Untergrund, weil ein Ring, den man nicht sieht, keiner
 * ist.
 */
export const FOKUS_RING_HELL =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream';

/**
 * Für Elemente in einem Kasten mit `overflow-hidden`.
 *
 * Die Tabellenzelle trägt es, damit `truncate` greift; ein außen
 * gezeichneter Ring wäre dort abgeschnitten. In A39 an Bildpunkten
 * gemessen, nicht aus der Klassenliste geschlossen.
 */
export const FOKUS_RING_INNEN = `${KERN} focus-visible:ring-inset`;
